// ---- Active-map scalars (plan 21) ----
// The setMapTerrain twin for the ACTIVE map's plain numbers: size-derived
// bounds and the env water level. One map is active per process — the client
// (which builds exactly one world, named by its `spawn` message) and every
// single-map context (tests, the studio catalog, module init before a map is
// chosen). The multi-map SERVER keeps per-map values on each MapRt
// (src/server/maps.ts) instead of reading these after boot.
//
// These are `export let` LIVE BINDINGS: only this module assigns them
// (setActiveMap), importers always read the current value. THE TRAP this
// module exists to avoid: deriving a module-init `const` from them elsewhere
// bakes the pre-spawn default — derive per call (terrain.ts's chunk math and
// carveShore pivot are functions for exactly this reason).
//
// Import discipline: this is a LEAF (type-only imports), so anything —
// terrain.ts included — may read it without cycles. load.ts hydrates it for
// the default map at module init via activateMap (the same guarantee as
// setMapTerrain: no sampler runs before hydration).

import type { MapDoc, MapEnv } from "../content/schemas.ts";

/** The global waterline when a map's env doesn't override it. */
export const WATER_LEVEL_DEFAULT = -1.5;

/** Active map's square side length, world units. */
export let MAP_SIZE = 1080;
/** size / 2 — the map's half-extent. */
export let MAP_HALF = 540;
/** Hard movement clamp: half - 2 (the old BOUND/HALF, now per-map). */
export let BOUND = 538;
/** Water plane height — feeds the shore-carve, so banks re-derive with it. */
export let WATER_LEVEL = WATER_LEVEL_DEFAULT;
/** The active map's env block ({} when the doc has none). */
export let ACTIVE_ENV: MapEnv = {};
/** The active map's id ("" before hydration — load.ts sets it at module init). */
export let ACTIVE_MAP_ID = "";

/** Point the scalars at `doc`. Callers almost always want activateMap
 *  (content/load.ts), which also rehydrates the terrain layers + flat zones. */
export function setActiveMap(doc: MapDoc): void {
  ACTIVE_MAP_ID = doc.id;
  MAP_SIZE = doc.size;
  MAP_HALF = doc.size / 2;
  BOUND = doc.size / 2 - 2;
  WATER_LEVEL = doc.env?.waterLevel ?? WATER_LEVEL_DEFAULT;
  ACTIVE_ENV = doc.env ?? {};
}
